import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import ProductionFilters from './components/ProductionFilters';
import ProductionScanHero from './components/ProductionScanHero';
import OperatorLotQueue from './components/OperatorLotQueue';
import ManagerProductionOverview from './components/ManagerProductionOverview';
import ProductionLotManagerDrawer from './components/ProductionLotManagerDrawer';
import { useProductionTrackingData } from './hooks/useProductionTrackingData';

const ProductionTrackingDashboard = () => {
  const { t } = useTranslation();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [drawerBatch, setDrawerBatch] = useState(null);

  const {
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
    productionBatches,
    filteredBatches,
    readyBatchCount,
    workshopWideSearch,
    machines,
    personnel,
    loading,
    error,
    userRole,
    userRoleTypes,
    visibilityCtx,
    isManagerView,
    canOpenWorkshop,
    refreshAll,
    handleResetFiltersAndSearch,
    handleExport,
    batchHandlers,
  } = useProductionTrackingData();

  const breadcrumbItems = [
    {
      label: t('sidebar.nav.dashboard'),
      path: '/dashboard',
      icon: 'LayoutDashboard',
    },
    {
      label: t('sidebar.nav.production.productionTracking'),
      path: '/production-tracking-dashboard',
      icon: 'Factory',
    },
  ];

  return (
    <div className="flex h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-3 sm:px-4 py-4 md:py-6 max-w-[1400px] min-w-0">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4 md:mb-6">
              <div className="min-w-0 max-w-2xl">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight mb-2">
                  {t('productionTracking.pageTitle')}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {isManagerView
                    ? t('productionTracking.manager.pageSubtitle')
                    : t('productionTracking.operator.pageSubtitle')}
                </p>
              </div>
              {isManagerView && (
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Download"
                  onClick={handleExport}
                  className="min-touch-target w-full lg:w-auto"
                >
                  {t('productionTracking.exportCsv')}
                </Button>
              )}
            </div>

            <ProductionScanHero
              visibilityCtx={visibilityCtx}
              knownBatches={productionBatches}
            />

            <ProductionFilters
              filters={filters}
              onFilterChange={setFilters}
              onResetAll={handleResetFiltersAndSearch}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              machines={machines}
              cases={productionBatches}
              criticalCases={filteredBatches}
              readyCount={readyBatchCount}
              queueActive={!workshopWideSearch}
              onShowReady={handleResetFiltersAndSearch}
              showReadyFilter={!isManagerView}
              showAdvanced
            />

            {error ? (
              <div className="text-center py-12 rounded-xl border border-border bg-card">
                <p className="text-destructive mb-4">{error}</p>
                <Button onClick={refreshAll}>{t('productionTracking.retry')}</Button>
              </div>
            ) : isManagerView ? (
              <ManagerProductionOverview
                batches={productionBatches}
                filters={filters}
                searchQuery={searchQuery}
                userRole={userRole}
                canOpenWorkshop={canOpenWorkshop}
                visibilityCtx={visibilityCtx}
                loading={loading}
                onOpenLotDetails={setDrawerBatch}
              />
            ) : (
              <OperatorLotQueue
                batches={productionBatches}
                filteredBatches={filteredBatches}
                filters={filters}
                searchQuery={searchQuery}
                userRole={userRole}
                visibilityCtx={visibilityCtx}
                canOpenWorkshop={canOpenWorkshop}
                loading={loading}
              />
            )}
          </div>
        </main>
      </div>

      {drawerBatch && (
        <ProductionLotManagerDrawer
          batch={drawerBatch}
          machines={machines}
          personnel={personnel}
          userRole={userRole}
          userRoleTypes={userRoleTypes}
          canOpenWorkshop={canOpenWorkshop}
          batchHandlers={batchHandlers}
          onClose={() => setDrawerBatch(null)}
        />
      )}
    </div>
  );
};

export default ProductionTrackingDashboard;
